import { useRequestApi } from '@/common/_hooks/useRequestApi'
import { ApiResponse } from '@/common/_types/message'
import { Box, Tab, Typography } from '@mui/material'
import { SyntheticEvent, useEffect, useState } from 'react'
import { OilResourceType, OilType, oilResourceMap } from '../_types/OilPrice'
import { TUnitPrc } from '../_types/apiResponse'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'

type ComponentProps = {
  title: string
  descriptions: { [category: string]: string[] }
  isBig: boolean
}

type TUnitPrcType = Record<string, TUnitPrc[]>

const categories = { frght: '화물', taxi: '택시', bus: '버스' }

const displayOilTypes: OilType[] = ['D', 'L', 'H', 'C']

const formatPrice = (price?: number | null) => {
  if (typeof price !== 'number') return '-'

  return price.toLocaleString('ko-KR')
}

const getExtraPriceItems = (
  selectedCategory: string,
  key: OilType,
  data?: TUnitPrc,
) => {
  if (selectedCategory !== 'bus') return []

  if (key === 'D') {
    // 경유 카드: 고속우등버스(09,경유) + 전세버스(13,경유) - 둘 다 해당 유종(D) 값
    return [
      { label: '고속우등버스', price: data?.expressBusUntprc },
      { label: '전세버스', price: data?.charterBusUntprc },
    ]
  }

  if (key === 'C') {
    // CNG 카드: 전세버스(13,CNG)
    return [{ label: '전세버스', price: data?.charterBusUntprc }]
  }

  return []
}

const PriceSection = ({ title, isBig }: ComponentProps) => {
  const [selectedCategory, setSelectedCategory] = useState('frght')
  const { sendSimpleHttpRequest } = useRequestApi()
  const [tUnitPrc, setTUnitPrc] = useState<TUnitPrcType>({})

  const handleChange = (event: SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue)
  }

  useEffect(() => {
    ;(async () => {
      try {
        const response: ApiResponse = await sendSimpleHttpRequest(
          'GET',
          '/pbc/taoi/kus',
        )
        if (response && response.resultType === 'success' && response.data) {
          // 데이터 조회 성공시
          setTUnitPrc(response.data)
        } else {
          setTUnitPrc({})
          // 데이터가 없거나 실패
        }
      } catch (error) {
        // 에러시
        console.error('Error fetching data:', error)
      }
    })()
  }, [])

  const getPriceItem = (
    key: OilType,
    resource: OilResourceType,
    category: string,
  ) => {
    const data = tUnitPrc[category]?.find((item) => item.koiCd === key)
    if (!data) return null

    const extraItems = getExtraPriceItems(category, key, data)

    return (
      <Box
        className={`oil-price-card${isBig ? ' oil-price-card-big' : ''}`}
        key={key}
      >
        <Box className="oil-price-card-main">
          <Box className="oil-price-card-badge-wrap">
            <Box className="oil-price-card-title" style={resource.style}>
              {resource.name}
            </Box>
          </Box>
          <Box className="oil-price-card-info">
            <Typography className="oil-price-card-unit">
              단가({resource.unit})
            </Typography>
            <Typography className="oil-price-card-value">
              {formatPrice(data?.oilUntprc)}
              <Box className="oil-price-card-currency" component="span">
                원
              </Box>
            </Typography>
          </Box>
        </Box>

        {extraItems.length > 0 && (
          <Box
            className={`oil-price-card-extra-list${
              extraItems.length > 1 ? ' oil-price-card-extra-list-cols' : ''
            }`}
          >
            {extraItems.map((item) => (
              <Box className="oil-price-card-extra" key={item.label}>
                <Typography className="oil-price-card-extra-label">
                  {item.label}
                </Typography>
                <Typography className="oil-price-card-extra-value">
                  {formatPrice(item.price)}
                  <Box
                    className="oil-price-card-extra-currency"
                    component="span"
                  >
                    원
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box className="main-contents">
      <Box className="main-contents-box oil-price-contents-box">
        <Typography className="contents-box-title" variant="h1">
          {title}
        </Typography>

        <Box className="contents-box-con marT20 oil-price-contents">
          {/* 라운드형 탭 시작 */}
          <TabContext value={selectedCategory}>
            <Box className="tabs-round-type oil-info-tabs-type">
              <TabList
                className="tab-list"
                onChange={handleChange}
                aria-label={`${title} 탭 그룹`}
              >
                {Object.entries(categories).map(([category, categoryName]) => (
                  <Tab key={category} label={categoryName} value={category} />
                ))}
              </TabList>

              <Box className="tab-content">
                {Object.keys(categories).map((category) => (
                  <TabPanel key={category} value={category}>
                    <Box className="oil-price-card-list">
                      {displayOilTypes.map((key) =>
                        getPriceItem(key, oilResourceMap[key], category),
                      )}
                    </Box>
                  </TabPanel>
                ))}
              </Box>
            </Box>
          </TabContext>
          {/* 라운드형 탭 끝 */}
        </Box>
      </Box>
    </Box>
  )
}

export default PriceSection
